import { ReactElement } from "react";

import { Icon, IconSize } from "@/DesignSystem";

import styles from "./FloatingActionButton.module.scss";

import SparkleFillIcon from "@/DesignSystem/Atoms/Icons/Types/Sparkle/sparkle-fill.svg";

type FloatingActionButtonSize = "xs" | "sm" | "md" | "lg";

interface IFloatingActionButtonProps {
    /**
     * The optional button ID.
     */
    id?: string;

    /**
     * The size of the label and button.
     */
    size?: FloatingActionButtonSize;

    /**
     * An ID to target in frontend tests.
     */
    dataCy?: string;

    /**
     * Whether to disable the button click event.
     */
    disabled?: boolean;

    /**
     * A callback to invoke when the button is clicked.
     */
    onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

    /**
     * A callback to invoke when the button is hovered.
     */
    onHover?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

    /**
     * The label that will be rendered inside the button
     */
    label?: string;
}

const buttonSizeMapper: Record<FloatingActionButtonSize, string> = {
    "lg": styles.lg,
    "md": styles.md,
    "sm": styles.sm,
    "xs": styles.xs
} as const;

const iconSizeMapper: Record<FloatingActionButtonSize, IconSize> = {
    "lg": "md",
    "md": "md",
    "sm": "md",
    "xs": "sm"
} as const;

/**
 * The floating action button (FAB).
 * @param {IFloatingActionButtonProps} param The props used to render the floating action button.
 * @returns The rendered component.
 */
function FloatingActionButton({
    id,
    size = "md",
    label,
    dataCy,
    disabled,
    onClick,
    onHover
}: Readonly<IFloatingActionButtonProps>): ReactElement {
    return (
        <button
            onClick={e => onClick?.(e)}
            onMouseOver={e => onHover?.(e)}
            disabled={disabled}
            data-cy={dataCy}
            id={id}
            className={`${styles.FAB} ${buttonSizeMapper[size]}`}
        >
            <Icon size={iconSizeMapper[size]}><SparkleFillIcon /></Icon>
            <span className={styles.label}>{label}</span>
        </button>
    );
}

export {
    FloatingActionButton,
    FloatingActionButtonSize,
    IFloatingActionButtonProps
};
